import { not } from "@components/smart/FieldInfo";
import { getAddressFields, getAddressGroup, PhoneNumberDef } from "@components/smart/GeneralFieldDefinition";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    CompanyEntity,
    EntitySetName,
    EntityTypeName,
    PrHealthInsuranceCompanyEntity,
    PrPppzSubmissionCompanyEntity,
    PrPppzSubmissionDetailEntity,
    PrPppzSubmissionEntity
} from "@odata/GeneratedEntityTypes";
import { PrSubmissionTypeCode } from "@odata/GeneratedEnums";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import { getPayrollCurrencyFieldDef } from "@pages/payroll/payroll.utils";
import {
    getDefinitions as commonDef,
    getSubmissionFormDef,
    IGetSubmissionDefArgs,
    PrSubmissionGroup
} from "@pages/payroll/submission/Submission.def";
import { CSSZTranslationFiles } from "@pages/payroll/submission/submissionTypes/PrCSSZSubmission.utils";
import PrPppzSubmissionFormView from "@pages/payroll/submission/submissionTypes/PrPppzSubmissionFormView";
import { isNewEntity } from "@utils/definition.utils";
import { forEachKey } from "@utils/general";
import i18next from "i18next";
import { cloneDeep } from "lodash";

import { IAppContext } from "../../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType, ValidatorType } from "../../../../enums";
import { addFormDef, addFormGroup, addRowsToGroup } from "../../../../views/formView/Form.utils";

export const PeriodLocalContextPath = BindingContext.localContext("period");

export const getDefinitions: IGetDefinition = (context: IAppContext, args?: IGetSubmissionDefArgs): IDefinition => {
    const d = cloneDeep(getSubmissionFormDef(PrPppzSubmissionFormView, context, {
        ...args,
        type: PrSubmissionTypeCode.EmployersHealthInsurancePaymentOverview,
        subType: null
    }));

    d.form.additionalProperties = [
        ...d.form.additionalProperties,
        { id: createPath(PrPppzSubmissionEntity.Detail, PrPppzSubmissionDetailEntity.Company, PrPppzSubmissionCompanyEntity.Company, CompanyEntity.Id) }
    ];

    // **************** Payment fields ****************
    addFormDef(d, PeriodLocalContextPath, {
        type: FieldType.MonthYear,
        isRequired: true,
        isReadOnly: not(isNewEntity),
        // display validation messages, etc. from Month field
        backendPath: createPath(PrPppzSubmissionEntity.Detail, PrPppzSubmissionDetailEntity.Month),
        label: i18next.t("Submission:PppzForm.Period"),
        additionalProperties: [
            { id: createPath(PrPppzSubmissionEntity.Detail, PrPppzSubmissionDetailEntity.Month) },
            { id: createPath(PrPppzSubmissionEntity.Detail, PrPppzSubmissionDetailEntity.Year) }
        ]
    });
    addFormDef(d, createPath(PrPppzSubmissionEntity.Detail, PrPppzSubmissionDetailEntity.EmployeeCount), {
        type: FieldType.NumberInput,
        fieldSettings: {
            min: 0,
            showSteppers: true
        },
        validator: {
            type: ValidatorType.Number,
            settings: {
                min: 0,
                excludeMin: true
            }
        },
    });
    addFormDef(d, createPath(PrPppzSubmissionEntity.Detail, PrPppzSubmissionDetailEntity.AssessmentBaseSum), {
        ...getPayrollCurrencyFieldDef()
    });
    addFormDef(d, createPath(PrPppzSubmissionEntity.Detail, PrPppzSubmissionDetailEntity.InsuranceSum), {
        label: i18next.t("Submission:PppzForm.InsuranceSum"),
        ...getPayrollCurrencyFieldDef()
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.AdditionalInfo,
        title: i18next.t("Submission:PppzForm.PaymentGroup"),
        rows: [[
            { id: PeriodLocalContextPath },
            { id: createPath(PrPppzSubmissionEntity.Detail, PrPppzSubmissionDetailEntity.EmployeeCount) },
            { id: createPath(PrPppzSubmissionEntity.Detail, PrPppzSubmissionDetailEntity.AssessmentBaseSum) },
            { id: createPath(PrPppzSubmissionEntity.Detail, PrPppzSubmissionDetailEntity.InsuranceSum) },
        ]]
    });

    // **************** Add company / employer fields ****************
    const pathToCompany = createPath(PrPppzSubmissionEntity.Detail, PrPppzSubmissionDetailEntity.Company);
    addFormDef(d, createPath(pathToCompany, PrPppzSubmissionCompanyEntity.LegalName), {
        width: BasicInputSizes.XL
    });
    addFormDef(d, createPath(pathToCompany, PrPppzSubmissionCompanyEntity.LegalNumber), {});

    const companyAddressFields = getAddressFields(pathToCompany, {}, {
        addHouseStreetNumber: true,
        withoutCountry: true,
        multiline: false
    });
    forEachKey(companyAddressFields, (fieldId) => {
        addFormDef(d, fieldId, companyAddressFields[fieldId]);
    });

    addFormDef(d, createPath(pathToCompany, PrPppzSubmissionCompanyEntity.InsurancePayerNumber), {});
    addFormDef(d, createPath(pathToCompany, PrPppzSubmissionCompanyEntity.PhoneNumber), {
        ...PhoneNumberDef
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.Employer,
        title: i18next.t("Submission:Form.EmployerIdentifier"),
        rows: [[
            { id: createPath(pathToCompany, PrPppzSubmissionCompanyEntity.LegalName) },
            { id: createPath(pathToCompany, PrPppzSubmissionCompanyEntity.LegalNumber) },
            ...(getAddressGroup(pathToCompany, {
                multiline: false,
                withoutCountry: true,
                addHouseStreetNumber: true
            })[0]),
            { id: createPath(pathToCompany, PrPppzSubmissionCompanyEntity.InsurancePayerNumber) },
            { id: createPath(pathToCompany, PrPppzSubmissionCompanyEntity.PhoneNumber) },
        ]]
    });

    /* enhance main group with institution */
    // **************** Institution ****************
    addFormDef(d, createPath(PrPppzSubmissionEntity.Detail, PrPppzSubmissionDetailEntity.HealthInsuranceCompany), {
        type: FieldType.ComboBox,
        label: i18next.t("Submission:PppzForm.HealthInsuranceCompany"),
        width: BasicInputSizes.L,
        isReadOnly: not(isNewEntity),
        fieldSettings: {
            displayName: PrHealthInsuranceCompanyEntity.ShortName,
            shouldDisplayAdditionalColumns: true,
        },
        additionalProperties: [{ id: PrHealthInsuranceCompanyEntity.Name }],
        columns: [
            { id: PrHealthInsuranceCompanyEntity.ShortName },
            { id: PrHealthInsuranceCompanyEntity.Name }
        ]
    });

    addRowsToGroup(d, PrSubmissionGroup.Main, [[
        { id: createPath(PrPppzSubmissionEntity.Detail, PrPppzSubmissionDetailEntity.HealthInsuranceCompany) },
    ]]);

    return {
        ...d,
        entitySet: EntitySetName.PrPppzSubmissions
    };
};

getDefinitions.translationFiles = [
    ...commonDef.translationFiles,
    ...CSSZTranslationFiles,
];
setDefByEntityType(EntityTypeName.PrPppzSubmission, getDefinitions);